import React from 'react'
import { BlueInput, InputWrapper } from './textInput'

type SelectOption<T> = {
  name: string
  value: T
  initialOnly?: boolean
}

interface SelectProps<T = string, V = SelectOption<T>> {
  /** The options to display in the select dropdown */
  options: V[]
  /** The currently selected option */
  value: V
  /** Callback function when the selected option changes */
  onChange: (value: V) => void
  /** Callback function when the select is opened or closed */
  onOpen?: (open: boolean) => void
  /** Additional class names for styling */
  className?: string
  /** Small size variant */
  small?: boolean
  /** Icon to display in the select */
  icon?: React.ReactNode
  /** Additional icons to display on the right side of the select */
  rightIcons?: React.ReactNode
  /** Whether the select is disabled, greyed out, and not clickable */
  disabled?: boolean
  /** Shorthand for the blue style variant */
  blue?: boolean
  /** Icon to show on the right as the dropdown indicator arrow */
  arrow?: React.ReactNode
  /** Additional classes to apply to the outer container */
  outerClasses?: string
}

/**
 * forwardRef is not typed correctly for components that use generics
 */
function hackyForwardRefTypeFixForSelect<T, P = {}>(
  render: (props: P, ref: React.Ref<T>) => React.ReactNode
): (props: P & React.RefAttributes<T>) => React.ReactNode {
  return React.forwardRef(render as any) as any
}

/**
 * Select component that renders a dropdown select input with options.
 * This is a wrapper around the default HTML select element with additional features and styling.
 */
export const Select = hackyForwardRefTypeFixForSelect(
  <T extends number | string, V extends SelectOption<T> = SelectOption<T>>(
    {
      arrow,
      small,
      options,
      value,
      onChange,
      className,
      icon,
      rightIcons,
      disabled,
      blue,
      outerClasses,
      ...rest
    }: SelectProps<T, V>,
    ref: React.Ref<HTMLDivElement>
  ) => {
    if (value === undefined) return null

    const disabledClass = disabled ? 'opacity-50 !cursor-not-allowed' : ''
    const sizeClass = small ? 'px-2 py-1' : `py-[6px] ${icon ? 'pl-10' : ''}`

    return (
      <InputWrapper
        ref={ref}
        {...rest}
        innerClasses={className}
        className={outerClasses}
        rightIcons={rightIcons || arrow}
        icon={icon}
      >
        <select
          disabled={disabled}
          value={value.value}
          onFocus={() => rest.onOpen && rest.onOpen(true)}
          onBlur={() => rest.onOpen && rest.onOpen(false)}
          onChange={(event) => onChange(options.find((option) => option.value.toString() === event.target.value)!)}
          className={`${className} ${blue ? BlueInput : ''} ${sizeClass} ${disabledClass} ${arrow ? 'appearance-none bg-none' : ''} w-full-in-wrapper cursor-pointer rounded border-gray-200 placeholder-gray-400 focus:border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-opacity-50`}
        >
          {options
            .filter((o) => value.value === o.value || !o.initialOnly)
            .map((option) => (
              <option key={option.value} value={option.value} disabled={option.initialOnly}>
                {option.name}
              </option>
            ))}
        </select>
      </InputWrapper>
    )
  }
)

export const SimpleSelect: React.FC<
  Omit<SelectProps, 'value' | 'options' | 'onChange'> & {
    value: string
    options: string[]
    onChange: (value: string) => void
  }
> = ({ value, options, onChange, ...props }) => (
  <Select
    {...props}
    value={{ value, name: value }}
    options={options.map((o) => ({
      value: o,
      name: o,
    }))}
    onChange={(opt) => onChange(opt.value)}
  />
)
